(() => {
  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const elementIsVisible = (element) => {
    if (!element) return false;
    const style = getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  };

  const setupStickyOrder = () => {
    const bar = document.querySelector('[data-bhakti-sticky-atc]');
    const primaryButton = document.querySelector('[data-bhakti-cod-order]');
    const stickyButton = bar?.querySelector('[data-bhakti-sticky-order]');
    const stickyQuantity = bar?.querySelector('[data-bhakti-sticky-quantity-input]');
    const decreaseButton = bar?.querySelector('[data-bhakti-sticky-quantity-action="decrease"]');
    const increaseButton = bar?.querySelector('[data-bhakti-sticky-quantity-action="increase"]');
    if (!bar || !primaryButton || !stickyButton || bar.dataset.bhaktiStickyBound === 'true') return;

    bar.dataset.bhaktiStickyBound = 'true';
    const productForm = primaryButton.closest('product-form');
    const form = primaryButton.closest('form');
    const sectionId = productForm?.dataset.sectionId || '';
    const variantInput = form?.querySelector('input[name="id"]');
    const price = bar.querySelector('[data-bhakti-sticky-price]');
    const mrpRow = bar.querySelector('[data-bhakti-sticky-mrp-row]');
    const mrp = bar.querySelector('[data-bhakti-sticky-mrp]');
    const discount = bar.querySelector('[data-bhakti-sticky-discount]');
    const image = bar.querySelector('[data-bhakti-sticky-image]');
    const fallbackImage = image ? {
      src: image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
      sizes: image.getAttribute('sizes') || ''
    } : null;
    let variants = [];
    try {
      variants = JSON.parse(bar.querySelector('[data-bhakti-sticky-variants]')?.textContent || '[]');
    } catch {
      variants = [];
    }
    const variantsById = new Map(variants.map((variant) => [String(variant.id), variant]));
    const money = (cents) => new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(Math.max(0, Number(cents) || 0) / 100);
    const setText = (element, value) => {
      if (element && element.textContent.trim() !== value) element.textContent = value;
    };
    const setHidden = (element, hidden) => {
      if (element && element.hidden !== hidden) element.hidden = hidden;
    };

    let scheduled = false;
    let boundQuantityInput;

    const previewOffset = () => {
      const candidates = document.querySelectorAll(
        '#PBarNextFrame, iframe[id*="preview" i], iframe[src*="theme-editor" i], iframe[src*="preview-bar" i]'
      );
      let offset = 0;
      candidates.forEach((element) => {
        if (!elementIsVisible(element)) return;
        const rect = element.getBoundingClientRect();
        const touchesBottom = rect.bottom >= window.innerHeight - 4 && rect.top < window.innerHeight;
        if (touchesBottom) offset = Math.max(offset, Math.min(rect.height, 100));
      });
      document.documentElement.style.setProperty('--bhakti-preview-offset', offset + 'px');
      return offset;
    };

    const cartIsOpen = () => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && (
        drawer.classList.contains('active') ||
        drawer.hasAttribute('open') ||
        drawer.getAttribute('aria-hidden') === 'false'
      )) return true;
      return Boolean(document.querySelector(
        'dialog[open][aria-label*="cart" i], .drawer.active, .cart-drawer.active'
      ));
    };

    const bottomSurfaceIsOpen = () => {
      const openModal = Array.from(document.querySelectorAll(
        'dialog[open], [aria-modal="true"]:not([aria-hidden="true"])'
      )).some(elementIsVisible);
      if (openModal) return true;
      const candidates = document.querySelectorAll(
        '[id*="cookie" i], [class*="cookie" i], [id*="consent" i], [class*="consent" i]'
      );
      return Array.from(candidates).some((element) => {
        if (!elementIsVisible(element)) return false;
        const style = getComputedStyle(element);
        const rect = element.getBoundingClientRect();
        const anchored = style.position === 'fixed' || style.position === 'sticky';
        return anchored && rect.bottom >= window.innerHeight - 4 && rect.height >= 40;
      });
    };

    const findQuantityInput = () => {
      if (sectionId) {
        const sectionQuantity = document.getElementById('Quantity-' + sectionId);
        if (sectionQuantity) return sectionQuantity;
      }
      if (form?.id) {
        const linkedQuantity = document.querySelector(
          'input[name="quantity"][form="' + CSS.escape(form.id) + '"]'
        );
        if (linkedQuantity) return linkedQuantity;
      }
      return form?.querySelector('input[name="quantity"]') || null;
    };

    const ensureQuantityInput = () => {
      const existing = findQuantityInput();
      if (existing || !form) return existing;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'quantity';
      input.value = String(Math.max(1, parseInt(stickyQuantity?.value, 10) || 1));
      input.setAttribute('data-bhakti-sticky-created-quantity', 'true');
      form.appendChild(input);
      return input;
    };

    const clampQuantity = (input, value) => {
      const min = Math.max(1, parseInt(input?.min || input?.dataset.min, 10) || 1);
      const maxValue = parseInt(input?.max || input?.dataset.max, 10);
      const max = Number.isFinite(maxValue) ? Math.max(min, maxValue) : Number.POSITIVE_INFINITY;
      const step = Math.max(1, parseInt(input?.step, 10) || 1);
      let next = parseInt(value, 10);
      if (!Number.isFinite(next)) next = min;
      next = min + Math.round((next - min) / step) * step;
      return Math.min(max, Math.max(min, next));
    };

    const syncStickyQuantity = (input = ensureQuantityInput()) => {
      if (!stickyQuantity) return 1;
      const quantity = clampQuantity(input, input?.value || stickyQuantity.value);
      if (String(stickyQuantity.value) !== String(quantity)) stickyQuantity.value = String(quantity);
      stickyQuantity.min = String(Math.max(1, parseInt(input?.min || input?.dataset.min, 10) || 1));
      if (input?.max || input?.dataset.max) stickyQuantity.max = input.max || input.dataset.max;
      else stickyQuantity.removeAttribute('max');
      if (input?.step) stickyQuantity.step = input.step;
      if (decreaseButton) {
        const shouldDisableDecrease = quantity <= Number(stickyQuantity.min || 1);
        if (decreaseButton.disabled !== shouldDisableDecrease) {
          decreaseButton.disabled = shouldDisableDecrease;
        }
      }
      if (increaseButton) {
        const max = Number(stickyQuantity.max);
        const shouldDisableIncrease = Number.isFinite(max) && quantity >= max;
        if (increaseButton.disabled !== shouldDisableIncrease) {
          increaseButton.disabled = shouldDisableIncrease;
        }
      }
      return quantity;
    };

    const setProductQuantity = (value) => {
      const input = ensureQuantityInput();
      const quantity = clampQuantity(input, value);
      if (input) {
        input.value = String(quantity);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (stickyQuantity) stickyQuantity.value = String(quantity);
      syncStickyQuantity(input);
      return quantity;
    };

    const bindQuantityInput = () => {
      const input = ensureQuantityInput();
      if (!input) return;
      if (boundQuantityInput !== input) {
        boundQuantityInput = input;
        input.addEventListener('input', () => syncStickyQuantity(input));
        input.addEventListener('change', () => syncStickyQuantity(input));
      }
      syncStickyQuantity(input);
    };

    const sizedImage = (src) => {
      if (!src) return '';
      const source = String(src);
      const parts = source.split('?');
      const parameters = new URLSearchParams(parts.slice(1).join('?'));
      parameters.set('width', '160');
      return parts[0] + '?' + parameters.toString();
    };

    const restoreFallbackImage = () => {
      if (!image || !fallbackImage) return;
      image.src = fallbackImage.src;
      if (fallbackImage.srcset) image.setAttribute('srcset', fallbackImage.srcset);
      else image.removeAttribute('srcset');
      if (fallbackImage.sizes) image.setAttribute('sizes', fallbackImage.sizes);
      else image.removeAttribute('sizes');
    };

    const syncVariant = () => {
      const variantId = String(variantInput?.value || bar.dataset.bhaktiCurrentVariant || '');
      const variant = variantsById.get(variantId);
      if (!variant) return;
      const unitPrice = Number(variant.price || 0);
      const comparePrice = Number(variant.compare_at_price || 0);
      const discountPercent = comparePrice > unitPrice
        ? Math.round(((comparePrice - unitPrice) / comparePrice) * 100)
        : 0;

      bar.dataset.bhaktiCurrentVariant = variantId;
      bar.dataset.bhaktiUnitPrice = String(unitPrice);
      bar.dataset.bhaktiComparePrice = String(comparePrice);
      setText(price, money(unitPrice));
      setText(mrp, money(comparePrice));
      setHidden(mrpRow, discountPercent <= 0);
      if (discount) {
        setHidden(discount, discountPercent <= 0);
        setText(discount, discountPercent + '% OFF');
      }

      const variantImage = variant.featured_image?.src || variant.featured_image?.url || '';
      if (image && variantImage) {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = sizedImage(variantImage);
      } else {
        restoreFallbackImage();
      }

      const unavailable = variant.available === false;
      if (stickyButton.disabled !== unavailable) stickyButton.disabled = unavailable;
      if (stickyButton.getAttribute('aria-disabled') !== String(unavailable)) {
        stickyButton.setAttribute('aria-disabled', String(unavailable));
      }
    };

    const update = () => {
      scheduled = false;
      bindQuantityInput();
      syncVariant();
      const buttonRect = primaryButton.getBoundingClientRect();
      const disabled = primaryButton.disabled || primaryButton.getAttribute('aria-disabled') === 'true';
      const onMobile = window.matchMedia('(max-width: 749px)').matches;
      const hasPassedButton = buttonRect.bottom < 0;
      const blocked =
        cartIsOpen() ||
        bottomSurfaceIsOpen() ||
        document.body.classList.contains('bhakti-external-checkout-visible');
      previewOffset();
      const show = onMobile && hasPassedButton && !disabled && !blocked;
      setHidden(bar, !show);
      if (document.body.classList.contains('bhakti-sticky-order-visible') !== show) {
        document.body.classList.toggle('bhakti-sticky-order-visible', show);
      }
    };

    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(update);
    };

    decreaseButton?.addEventListener('click', () => {
      const input = ensureQuantityInput();
      const step = Math.max(1, parseInt(input?.step || stickyQuantity?.step, 10) || 1);
      setProductQuantity(Number(stickyQuantity?.value || input?.value || 1) - step);
    });
    increaseButton?.addEventListener('click', () => {
      const input = ensureQuantityInput();
      const step = Math.max(1, parseInt(input?.step || stickyQuantity?.step, 10) || 1);
      setProductQuantity(Number(stickyQuantity?.value || input?.value || 1) + step);
    });
    stickyQuantity?.addEventListener('change', () => setProductQuantity(stickyQuantity.value));
    stickyButton.addEventListener('click', () => {
      if (primaryButton.disabled || primaryButton.getAttribute('aria-disabled') === 'true') return;
      setProductQuantity(stickyQuantity?.value || 1);
      primaryButton.click();
    });
    form?.addEventListener('change', schedule, true);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    window.visualViewport?.addEventListener('resize', schedule, { passive: true });
    window.visualViewport?.addEventListener('scroll', schedule, { passive: true });
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden', 'aria-disabled', 'disabled']
    });
    if (variantInput) {
      new MutationObserver(schedule).observe(variantInput, {
        attributes: true,
        attributeFilter: ['value', 'disabled']
      });
    }
    update();
  };

  const setupPincode = () => {
    document.querySelectorAll('[data-bhakti-pincode-form]').forEach((form) => {
      if (form.dataset.bhaktiPincodeBound === 'true') return;
      form.dataset.bhaktiPincodeBound = 'true';

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const input = form.querySelector('input');
        const result = form.querySelector('[data-bhakti-pincode-result]');
        if (!result) return;

        const value = input?.value.trim() || '';
        if (!/^\d{6}$/.test(value)) {
          result.textContent = 'Enter a valid 6-digit Indian PIN code.';
          result.classList.add('is-error');
          result.style.color = '#9a3029';
          result.style.fontWeight = '750';
          return;
        }

        result.classList.remove('is-error');
        result.style.color = '#1f7a4d';
        result.style.fontWeight = '850';
        result.textContent = '✓ Valid PIN code — Delivery & COD availability will be confirmed at checkout';
      });
    });
  };

  const setupFaq = () => {
    document.querySelectorAll('[data-bhakti-faq]').forEach((group) => {
      if (group.dataset.bhaktiFaqBound === 'true') return;
      group.dataset.bhaktiFaqBound = 'true';

      group.addEventListener('toggle', (event) => {
        if (!event.target.open) return;
        group.querySelectorAll('details[open]').forEach((item) => {
          if (item !== event.target) item.removeAttribute('open');
        });
      }, true);
    });
  };

  ready(() => {
    setupStickyOrder();
    setupPincode();
    setupFaq();
  });
})();